import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronRight, ChevronLeft } from 'lucide-react';
const volumes = [{
  number: '01',
  title: 'THE FUNCTIONAL FOUNDATION',
  subtitle: 'The Complete Guide to Movement Patterns & Programming Principles',
  desc: 'The essential starting point. Master the 7 fundamental movement patterns, learn how to assess your athletes before training and build progressive programs from scratch — with ready-to-use session templates and weekly planning frameworks included.',
  pages: '80–100 pages',
  color: 'from-blue-600 to-blue-900',
  image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '02',
  title: 'THE CROSS TRAINING BIBLE',
  subtitle: 'High-Intensity Programming for Coaches Who Want Results',
  desc: 'Everything you need to design, scale and deliver high-intensity cross training sessions safely and effectively. From WOD design to scaling strategies for every fitness level — this volume turns you into a confident cross training specialist.',
  pages: '80–100 pages',
  color: 'from-orange-500 to-orange-800',
  image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '03',
  title: 'STRENGTH THAT MOVES',
  subtitle: 'Resistance & Functional Strength Programming for Real Athletes',
  desc: 'A deep dive into functional strength training — how to combine resistance work with movement quality. Program strength blocks that complement functional and cross training without compromising mobility or performance.',
  pages: '80–100 pages',
  color: 'from-zinc-500 to-zinc-800',
  image: 'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '04',
  title: 'MOBILITY & RECOVERY MASTERY',
  subtitle: "The Coach's Guide to Keeping Athletes Moving Longer",
  desc: "The volume most coaches overlook — and the one that separates good coaches from great ones. Mobility work, active recovery protocols, breathwork and the science of rest. Reduce dropout, prevent burnout and extend your athletes' longevity.",
  pages: '80–100 pages',
  color: 'from-cyan-600 to-cyan-900',
  image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '05',
  title: 'TRAIN EVERY BODY',
  subtitle: 'Functional Programming for Special Populations',
  desc: 'How to train beginners, seniors, postpartum women, overweight clients and athletes returning from inactivity. The confidence and tools to work with every type of athlete using functional and cross training methods adapted for each group.',
  pages: '80–100 pages',
  color: 'from-emerald-600 to-emerald-900',
  image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=1000&auto=format&fit=crop'
}, {
  number: '06',
  title: 'THE HIGH PERFORMANCE COACH',
  subtitle: 'Advanced Programming, Periodization & Athlete Monitoring',
  desc: 'For coaches ready to take their athletes — and their practice — to the next level. Advanced periodization models, performance testing, athlete tracking systems and complete 12-week transformation program frameworks. The volume that makes you truly stand out.',
  pages: '80–100 pages',
  color: 'from-purple-600 to-purple-900',
  image: 'https://images.unsplash.com/photo-1554244933-d876deb6b2ff?q=80&w=1000&auto=format&fit=crop'
}];
export function Volumes() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const toggleExpand = (idx: number) => {
    setExpandedIndex(expandedIndex === idx ? null : idx);
  };
  return <section className="py-12 md:py-24 px-4 bg-[#050505] relative overflow-hidden" data-id="element-214">
      <div className="max-w-6xl mx-auto" data-id="element-215">
        <div className="text-center mb-10 md:mb-16" data-id="element-216">
          <h2 className="text-3xl md:text-6xl font-bold mb-4 md:mb-6" data-id="element-217">
            THE COMPLETE{' '}
            <span className="text-brand-blue text-glow-blue" data-id="element-218">6-VOLUME</span>{' '}
            SYSTEM
          </h2>
          <p className="text-base md:text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-219">
            The foundation of everything. Six volumes of pure coaching gold.
            Stop reacting to whatever your athletes need in the moment and start
            building a program that truly transforms them.
          </p>
        </div>

        <div className="mb-12 md:mb-20" data-id="element-220">
          <img src="https://cdn.magicpatterns.com/uploads/ksvZ7GpczKeQRVKMp6NC8P/Mockup_Functional__Cross_Ready-Photoroom.png" alt="Functional & Cross Ready Bundle" className="w-full max-w-4xl mx-auto drop-shadow-2xl" data-id="element-221" />
        </div>

        {/* Rows of full-width banners */}
        <div className="relative w-full" data-id="element-222">
          <div className="flex flex-col gap-4 md:gap-6" data-id="element-223">
            {volumes.map((vol, idx) => <motion.div key={idx} layout onClick={() => toggleExpand(idx)} className="group w-full flex flex-col rounded-2xl overflow-hidden cursor-pointer bg-zinc-900 border border-zinc-800 hover:border-brand-blue/50 transition-colors" data-id="element-224">
                {/* 3:1 Banner Image Area */}
                <motion.div layout="position" className="relative w-full aspect-[2/1] md:aspect-[3/1] bg-zinc-800 overflow-hidden" data-id="element-225">
                  <img src={vol.image} alt={vol.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" data-id="element-226" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/20" data-id="element-227"></div>

                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end" data-id="element-228">
                    <div className="flex items-end gap-4" data-id="element-229">
                      <span className="text-6xl md:text-8xl font-bold text-white/20 font-oswald leading-none tracking-tighter" data-id="element-230">
                        {vol.number}
                      </span>
                      <div className="pb-1 md:pb-2" data-id="element-231">
                        <h3 className="text-2xl md:text-4xl font-bold text-white font-oswald tracking-wide mb-1 md:mb-2" data-id="element-232">
                          {vol.title}
                        </h3>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-blue/20 border border-brand-blue/30 text-brand-blue text-xs font-bold uppercase tracking-wider backdrop-blur-sm" data-id="element-233">
                          <BookOpen className="w-3.5 h-3.5" data-id="element-234" />
                          {vol.pages}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md p-2 rounded-full border border-white/10" data-id="element-235">
                    <motion.div animate={{
                  rotate: expandedIndex === idx ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-236">
                      <ChevronDownIcon className="w-5 h-5 text-white" data-id="element-237" />
                    </motion.div>
                  </div>
                </motion.div>

                {/* Expandable Description */}
                <AnimatePresence data-id="element-238">
                  {expandedIndex === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden bg-zinc-950" data-id="element-239">
                      <div className="p-6 md:p-8 border-t border-zinc-800" data-id="element-240">
                        <h4 className="text-lg md:text-xl font-semibold text-brand-orange mb-4" data-id="element-241">
                          {vol.subtitle}
                        </h4>
                        <p className="text-zinc-300 text-sm md:text-base leading-relaxed" data-id="element-242">
                          {vol.desc}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
function ChevronDownIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-243">
      <path d="m6 9 6 6 6-6" data-id="element-244" />
    </svg>;
}